<template>
  <div class="navbar-container d-flex content align-items-center">
    <!-- Nav Menu Toggler -->
    <ul class="nav navbar-nav d-xl-none">
      <li class="nav-item">
        <b-link class="nav-link" @click="toggleVerticalMenuActive">
          <feather-icon icon="MenuIcon" size="21" />
        </b-link>
      </li>
    </ul>

    <div class="bookmark-wrapper align-items-center flex-grow-1 d-none d-lg-flex text-white">
      <div class="d-flex justify-content-between align-items-center mt-2">
        <span class="font-weight-bold">Menu Collapsed</span>
        <b-form-checkbox
          v-model="isVerticalMenuCollapsed"
          name="is-vertical-menu-collapsed"
          class="mr-0 custom-checkbox"
          inline
        />
      </div>

      <!-- Menu Visibility -->
      <div class="d-flex justify-content-between align-items-center mt-2">
        <span class="font-weight-bold">Menu Hidden</span>
        <b-form-checkbox
          v-model="isNavMenuHidden"
          name="is-menu-visible"
          class="mr-0 custom-checkbox"
          inline
        />
      </div>

      <!-- Admin: View As -->
      <div v-if="isAdmin && adminViewEnabled" class="d-flex justify-content-between align-items-center mt-1" style="margin-left:16px;">
        <span class="font-weight-bold mr-1">View As</span>
        <v-select
          v-model="selectedImpersonateUser"
          :options="impersonateUserOptions"
          :reduce="opt => opt.value"
          label="text"
          placeholder="Select user..."
          style="min-width:160px; background:#fff; border-radius:4px;"
          @input="startImpersonation"
        />
      </div>

      <!-- Impersonation indicator -->
      <div v-if="isImpersonating" class="d-flex align-items-center mt-1" style="margin-left:16px;">
        <b-badge variant="warning" class="mr-1">Viewing as: {{ impersonatedName }}</b-badge>
        <b-button size="sm" class="text-white" @click="exitImpersonation">Exit</b-button>
      </div>
    </div>


    <div class="align-items-center flex-grow-1 d-none text-center justify-content-center d-lg-flex" v-if="userData.company != undefined && userData.company != null">
      <b-img-lazy v-bind="mainProps" class="p-50" :src="baseURL + userData.company.logoLink" v-if="userData.company.logoLink != null" />
      <b-img-lazy v-bind="mainProps" class="p-50" :src="baseURL + 'company.png'" v-else />
      <h4 class="ml-2 text-white mt-1 p-1">{{ userData.company.name }}</h4>
    </div>

    <b-navbar-nav class="nav align-items-center ml-auto">
      <b-nav-item-dropdown right toggle-class="d-flex align-items-center dropdown-user-link" class="dropdown-user">
        <template #button-content>
          <div class="d-sm-flex d-none user-nav">
            <p class="user-name font-weight-bolder mb-0">{{ userData.name }} {{ userData.surname }}</p>
            <span class="user-status" v-if="userData.role == 'supermanager'">Super Manager</span>
            <span style="text-transform: capitalize;" class="user-status" v-else>{{ userData.role }}</span>
          </div>

          <b-avatar size="40" variant="light-primary" badge :src="profileURL" class="badge-minimal" badge-variant="success" />
        </template>

        <b-dropdown-item link-class="d-flex align-items-center" @click="navigateProfile">
          <feather-icon size="16" icon="UserIcon" class="mr-50" />
          <span>Profile</span>
        </b-dropdown-item>

        <b-dropdown-divider />

        <b-dropdown-item link-class="d-flex align-items-center" @click="navigateHelp">
          <feather-icon size="16" icon="HelpCircleIcon" class="mr-50" />
          <span>Help</span>
        </b-dropdown-item>

        <b-dropdown-divider />

        <b-dropdown-item link-class="d-flex align-items-center" @click="logout">
          <feather-icon size="16" icon="LogOutIcon" class="mr-50" />
          <span>
            <b-link class="alert-link mt-5 mb-5">
              Logout
            </b-link></span
          >
        </b-dropdown-item>
      </b-nav-item-dropdown>
    </b-navbar-nav>
  </div>
</template>

<script>
import { BLink, BNavbarNav, BNavItemDropdown, BDropdownItem, BDropdownDivider, BAvatar, BBadge, BButton } from 'bootstrap-vue';
import DarkToggler from '@core/layouts/components/app-navbar/components/DarkToggler.vue';
import useJwt from '@/auth/jwt/useJwt';
import router from '@/router';
import store from '@/store';
import axiosIns from '@/libs/axios';
import vSelect from 'vue-select';

export default {
  data() {
    return {
      baseURL: store.state.app.baseURL,
      email: localStorage.getItem('email'),
      userData: JSON.parse(localStorage.getItem('userData')),
      profileURL: store.state.app.profileURL + JSON.parse(localStorage.getItem('userData')).profile,

      mainProps: {
        blank: true,
        blankColor: '#FFF',
        height: 15,
        class: 'm1',
        block: true,
      },

      adminViewEnabled: false,
      impersonateUserOptions: [],
      selectedImpersonateUser: null,
    };
  },

  created() {
    this.userDetails();
    this.loadFeatures();
    if (this.isAdmin) {
      this.loadImpersonateUsers();
    }
  },

  methods: {
    backRouter() {
      router.go(-1);
    },

    nextRouter() {
      router.go(1);
    },

    navigateProfile() {
      if (router.currentRoute.name != 'user-profile') {
        this.$router.push({ name: 'user-profile' });
      }
    },

    userDetails() {
      axiosIns
        .get(`user/${this.userData.id}`)
        .then((res) => {
          this.userData = res.data;
        })
        .catch((error) => {
          console.log(error);
        });
    },

    loadFeatures() {
      axiosIns.get('features').then((res) => {
        this.adminViewEnabled = !!res.data.admin_view;
      }).catch(() => {});
    },

    loadImpersonateUsers() {
      axiosIns.get('user').then((res) => {
        this.impersonateUserOptions = (res.data.data || []).map((u) => ({
          value: u.id,
          text: `${u.name} ${u.surname}`,
        }));
      });
    },

    startImpersonation(userId) {
      if (!userId) return;
      axiosIns.post(`impersonate/${userId}`).then((res) => {
        localStorage.setItem(
          'adminSession',
          JSON.stringify({
            userData: localStorage.getItem('userData'),
            accessToken: localStorage.getItem(useJwt.jwtConfig.storageTokenKeyName),
            userPerms: localStorage.getItem('userPerms'),
          })
        );
        localStorage.setItem('userData', JSON.stringify(res.data.userData));
        localStorage.setItem(useJwt.jwtConfig.storageTokenKeyName, res.data.accessToken);
        localStorage.setItem('userPerms', JSON.stringify(res.data.userPerms));
        window.location.href = '/';
      });
    },

    exitImpersonation() {
      const saved = JSON.parse(localStorage.getItem('adminSession'));
      localStorage.setItem('userData', saved.userData);
      localStorage.setItem(useJwt.jwtConfig.storageTokenKeyName, saved.accessToken);
      localStorage.setItem('userPerms', saved.userPerms);
      localStorage.removeItem('adminSession');
      window.location.reload();
    },

    logout() {
      localStorage.removeItem(useJwt.jwtConfig.storageTokenKeyName);
      localStorage.removeItem(useJwt.jwtConfig.storageRefreshTokenKeyName);
      localStorage.removeItem('userData');
      localStorage.removeItem('adminSession');
      this.$router.push({ name: 'auth-login' });
    },

    navigateHelp() {
      this.$router.push({ name: 'helpdesk' });
    },
  },

  computed: {
    isVerticalMenuCollapsed: {
      get: () => store.state.verticalMenu.isVerticalMenuCollapsed,
      set: (val) => {
        store.commit('verticalMenu/UPDATE_VERTICAL_MENU_COLLAPSED', val);
      },
    },
    isNavMenuHidden: {
      get: () => store.state.appConfig.layout.menu.hidden,
      set: (val) => {
        store.commit('appConfig/UPDATE_NAV_MENU_HIDDEN', val);
      },
    },
    isAdmin() {
      const role = this.userData && this.userData.role;
      return role === 'admin' || role === 'supermanager';
    },
    isImpersonating() {
      return !!localStorage.getItem('adminSession');
    },
    impersonatedName() {
      const u = JSON.parse(localStorage.getItem('userData') || '{}');
      return `${u.name || ''} ${u.surname || ''}`.trim();
    },
  },

  components: {
    BLink,
    BNavbarNav,
    BNavItemDropdown,
    BDropdownItem,
    BDropdownDivider,
    BAvatar,
    BBadge,
    BButton,
    vSelect,

    // Navbar Components
    DarkToggler,
  },
  props: {
    toggleVerticalMenuActive: {
      type: Function,
      default: () => {},
    },
  },
};
</script>

<style scoped>
::v-deep .custom-checkbox .custom-control-input:checked ~ .custom-control-label::before {
  background-color: green !important;
  border-color: green !important;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
